import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef
} from 'react';
import CollapsableMenuView from '../view/CollapsableMenuView';

export const EXPANDED_MENU_SIZE = 300;
export const COLLAPSED_MENU_SIZE = 60;

/**
 *
 * @param {Array} menuItems - array of object represnting the items, each object should look like this: {title: "...", value: "...", ?icon: node}
 * @param {Boolean} initCollapsed - load the component collapsed (true) or expanded (false)
 * @param {Boolean} noResizeBtn - if false - won't show the resize button regardless of if it's collapsed or expanded
 * @param {Integer} expandedWidth - custom size in px for the menu when it's expanded
 * @param {Integer} collapsedWidth - custom size in px for the menu when it's collapsed
 * @param {Fn} onExpand - function that runs on expand event, gets new menu width (px) as argument
 * @param {Fn} onCollapse - function that runs on collapse event, gets new menu width (px) as argument
 * @param {String} additionalCSS - string contains CSS to be placed inside Slideable div
 * @param {Node} topEndPoint - Add anything to be placed at the top of the menu
 */
const CollapsableMenuEngine = forwardRef(
  (
    {
      menuItems = [],
      initCollapsed,
      noResizeBtn,
      expandedWidth = EXPANDED_MENU_SIZE,
      collapsedWidth = COLLAPSED_MENU_SIZE,
      onExpand = w => w,
      onCollapse = w => w,
      additionalCSS,
      value: controlledValue,
      setValue: setControlledValue,
      topEndPoint
    },
    ref
  ) => {
    const [isExpanded, setIsExpanded] = useState(!initCollapsed);
    const [menuWidth, setMenuWidth] = useState(
      initCollapsed ? collapsedWidth : expandedWidth
    );
    const [isMenuHovered, setIsMenuHovered] = useState(false);
    const [internalValue, setInternalValue] = useState('');

    const value = controlledValue ? controlledValue : internalValue;
    const setValue = setControlledValue ? setControlledValue : setInternalValue;

    useEffect(() => {
      if (isExpanded) {
        setMenuWidth(expandedWidth);
        onExpand(expandedWidth);
      } else {
        setMenuWidth(collapsedWidth);
        onCollapse(collapsedWidth);
      }
    }, [isExpanded]);

    useImperativeHandle(
      ref,
      () => ({
        setIsExpanded
      }),
      []
    );

    const viewProps = {
      menuItems,
      value,
      setValue,
      menuWidth,
      isExpanded,
      setIsExpanded,
      isMenuHovered,
      setIsMenuHovered,
      additionalCSS,
      noResizeBtn,
      topEndPoint
    };

    return <CollapsableMenuView {...viewProps} />;
  }
);

export default CollapsableMenuEngine;
